import { writable } from "svelte/store";
import { io, type Socket } from "socket.io-client";
import { API_URL } from "@/api";
import type { ServerPlayers, ServerTelemetry } from "@/api";
import { PlayerSnapshotFence } from "@/utils/player-snapshot-fence";
import { playersAreStale } from "@/utils/player-freshness";

export type ConsoleConnection = "idle" | "connecting" | "connected" | "reconnecting" | "error";

export const consoleConnection = writable<ConsoleConnection>("idle");
export const consoleLines = writable<string[]>([]);
export const consoleError = writable("");
export const consoleStatus = writable<string | null>(null);
export const consoleAgentOnline = writable<boolean | null>(null);
export const consoleTelemetry = writable<ServerTelemetry | null>(null);
export const consolePlayers = writable<ServerPlayers | null>(null);
export const consoleServerId = writable<string | null>(null);

let socket: Socket | null = null;
let subscribedServerId: string | null = null;
let subscriptionVersion = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryAttempt = 0;
let retryableAuthentication = false;
let pendingLogs: string[] | null = null;
let telemetryCapturedAt: number | null = null;
let playerFence: PlayerSnapshotFence | null = null;
const pendingEvents = new Set<string>();

interface SubscriptionReply {
  ok: boolean;
  backlog?: string[];
  status?: string;
  agentOnline?: boolean;
  telemetry?: ServerTelemetry;
  players?: ServerPlayers;
  error?: string;
  retryable?: boolean;
  retryAfterSec?: number;
}

function acceptTelemetry(payload: ServerTelemetry): boolean {
  const captured = Date.parse(payload.capturedAt);
  if (
    !Number.isFinite(captured) ||
    (telemetryCapturedAt !== null && captured <= telemetryCapturedAt)
  ) {
    return false;
  }
  telemetryCapturedAt = captured;
  return true;
}

function clearRetry(): void {
  if (retryTimer) {
    clearTimeout(retryTimer);
  }
  retryTimer = null;
}

function retrySubscription(
  serverId: string,
  current: Socket,
  retryAfterSec?: number,
  reconnect = false,
): void {
  ++subscriptionVersion;
  pendingLogs = null;
  subscribedServerId = null;
  consoleError.set("Не удалось загрузить консоль. Повторяем подключение…");
  consoleConnection.set("reconnecting");
  const retryAlreadyScheduledOrObsolete = retryTimer !== null || socket !== current;
  const connectionUnavailable =
    !retryAlreadyScheduledOrObsolete && current.disconnected && !reconnect;
  if (retryAlreadyScheduledOrObsolete || connectionUnavailable) {
    return;
  }
  // Reserve headroom so clients remain staggered even at the backoff ceiling.
  const backoff = Math.min(25_000, 1_000 * 2 ** Math.min(retryAttempt++, 5));
  const requested =
    typeof retryAfterSec === "number" && Number.isFinite(retryAfterSec) && retryAfterSec > 0
      ? Math.min(30_000, retryAfterSec * 1_000)
      : 0;
  const base = Math.max(backoff, requested);
  const delay = Math.min(30_000, base + Math.floor(Math.random() * base * 0.2));
  retryTimer = setTimeout(() => {
    retryTimer = null;
    if (socket === current) {
      if (reconnect && current.disconnected) {
        current.connect();
      } else if (!current.disconnected) {
        subscribe(serverId);
      }
    }
  }, delay);
}

function refuseSubscription(current: Socket, message: string): void {
  ++subscriptionVersion;
  retryableAuthentication = false;
  clearRetry();
  pendingLogs = null;
  subscribedServerId = null;
  current.disconnect();
  setError(message);
}

// A live batch may already be present at the end of the fetched history.
function mergeLogs(history: string[], live: string[]): string[] {
  let overlap = Math.min(history.length, live.length);
  while (
    overlap > 0 &&
    !live.slice(0, overlap).every((line, i) => line === history[history.length - overlap + i])
  ) {
    overlap--;
  }
  return [...history, ...live.slice(overlap)].slice(-500);
}

function setError(message: string): void {
  consoleError.set(message);
  consoleConnection.set("error");
}

function subscribe(serverId: string): void {
  const current = socket;
  if (!current || current.disconnected) {
    return;
  }
  clearRetry();
  const version = ++subscriptionVersion;
  pendingLogs = [];
  pendingEvents.clear();
  current
    .timeout(10_000)
    .emit("subscribe", { serverId }, (error: Error | null, reply?: SubscriptionReply) => {
      if (socket !== current || version !== subscriptionVersion || current.disconnected) {
        return;
      }
      if (error || !reply) {
        retrySubscription(serverId, current);
        return;
      }
      if (!reply.ok) {
        if (reply.retryable === true) {
          retryableAuthentication = true;
          retrySubscription(serverId, current, reply.retryAfterSec);
        } else {
          refuseSubscription(current, reply.error ?? "Не удалось открыть консоль");
        }
        return;
      }
      retryAttempt = 0;
      retryableAuthentication = false;
      subscribedServerId = serverId;
      consoleServerId.set(serverId);
      consoleLines.set(mergeLogs(reply.backlog ?? [], pendingLogs ?? []));
      pendingLogs = null;
      if (!pendingEvents.has("status")) {
        consoleStatus.set(reply.status ?? null);
      }
      if (!pendingEvents.has("agent")) {
        consoleAgentOnline.set(reply.agentOnline ?? false);
      }
      if (reply.telemetry && !pendingEvents.has("telemetry") && acceptTelemetry(reply.telemetry)) {
        consoleTelemetry.set(reply.telemetry);
      } else if (!reply.telemetry && !pendingEvents.has("telemetry")) {
        telemetryCapturedAt = null;
        consoleTelemetry.set(null);
      }
      const playersSnapshotEligible = reply.players && !pendingEvents.has("players");
      if (reply.players && playersSnapshotEligible && playerFence?.accept(reply.players)) {
        consolePlayers.set({ ...reply.players, stale: playersAreStale(reply.players) });
      }
      consoleError.set("");
      consoleConnection.set("connected");
    });
}

export function connect(serverId: string, token: string): void {
  disconnect();
  playerFence = new PlayerSnapshotFence(serverId);
  consoleServerId.set(serverId);
  consoleConnection.set("connecting");
  consoleLines.set([]);
  consoleStatus.set(null);
  consoleAgentOnline.set(null);
  consoleTelemetry.set(null);
  consolePlayers.set(null);
  consoleError.set("");

  socket = io(`${API_URL}/console`, {
    auth: { token },
    reconnection: true,
    reconnectionAttempts: Infinity,
  });

  const current = socket;
  socket.on(
    "auth-error",
    (payload: { error?: string; retryable?: boolean; retryAfterSec?: number }) => {
      if (socket !== current) {
        return;
      }
      if (payload?.retryable === true) {
        retryableAuthentication = true;
        retrySubscription(serverId, current, payload.retryAfterSec);
      } else {
        refuseSubscription(current, payload?.error ?? "Не удалось подтвердить доступ к консоли");
      }
    },
  );
  socket.on("connect", () => {
    consoleConnection.set("connecting");
    subscribe(serverId);
  });
  socket.on("disconnect", (reason: string) => {
    ++subscriptionVersion;
    clearRetry();
    pendingLogs = null;
    subscribedServerId = null;
    consoleAgentOnline.set(null);
    if (reason === "io server disconnect" && retryableAuthentication) {
      retrySubscription(serverId, current, undefined, true);
    } else if (reason === "io server disconnect") {
      setError("Панель закрыла соединение с консолью. Обновите страницу и войдите снова.");
    } else {
      consoleConnection.set("reconnecting");
    }
  });
  socket.on("connect_error", () =>
    setError(
      "Не удалось подключиться к консоли. Проверьте подключение к интернету. Панель попробует восстановить связь автоматически.",
    ),
  );
  socket.on("log", (payload: { serverId?: string; lines?: string[] }) => {
    if (payload.serverId !== serverId || !payload.lines?.length) {
      return;
    }
    if (pendingLogs) {
      pendingLogs = [...pendingLogs, ...payload.lines].slice(-500);
    }
    consoleLines.update((lines) => [...lines, ...payload.lines!].slice(-500));
  });
  socket.on("status", (payload: { serverId?: string; status?: string; error?: string }) => {
    if (payload.serverId !== serverId) {
      return;
    }
    pendingEvents.add("status");
    consoleStatus.set(payload.status ?? null);
    if (payload.error) {
      consoleError.set(payload.error);
    }
  });
  socket.on("agent", (payload: { serverId?: string; online?: boolean }) => {
    if (payload.serverId === serverId) {
      pendingEvents.add("agent");
      consoleAgentOnline.set(payload.online === true);
    }
  });
  socket.on("telemetry", (payload: ServerTelemetry) => {
    if (payload.serverId === serverId && acceptTelemetry(payload)) {
      pendingEvents.add("telemetry");
      consoleTelemetry.set(payload);
    }
  });
  socket.on("players", (payload: ServerPlayers) => {
    if (payload.serverId === serverId && playerFence?.accept(payload)) {
      pendingEvents.add("players");
      consolePlayers.set({ ...payload, stale: playersAreStale(payload) });
    } else if (payload.serverId === serverId) {
      consolePlayers.update((current) =>
        current ? { ...current, stale: playersAreStale(current) } : null,
      );
    }
  });
}

export function disconnect(): void {
  ++subscriptionVersion;
  retryAttempt = 0;
  retryableAuthentication = false;
  clearRetry();
  pendingLogs = null;
  pendingEvents.clear();
  playerFence = null;
  telemetryCapturedAt = null;
  socket?.removeAllListeners();
  socket?.disconnect();
  socket = null;
  subscribedServerId = null;
  consoleConnection.set("idle");
  consoleServerId.set(null);
  consoleAgentOnline.set(null);
  consoleTelemetry.set(null);
  consolePlayers.set(null);
}

export function send(line: string): Promise<void> {
  const value = line.trim();
  const current = socket;
  if (!value || !current || !subscribedServerId || current.disconnected) {
    return Promise.reject(new Error("Консоль сейчас недоступна"));
  }
  return new Promise((resolve, reject) => {
    // The agent can take up to 60 seconds to acknowledge. Never retry input:
    // a missing acknowledgement does not prove that the command was not run.
    current
      .timeout(65_000)
      .emit(
        "input",
        { serverId: subscribedServerId, line: value },
        (error: Error | null, reply?: { ok: boolean; error?: string }) => {
          if (!error && reply?.ok) {
            resolve();
          } else {
            const message =
              error || !reply
                ? "Нет подтверждения выполнения команды. Проверьте вывод консоли перед повторной отправкой."
                : (reply.error ?? "Команда не отправлена");
            if (socket === current) {
              consoleError.set(message);
            }
            reject(new Error(message));
          }
        },
      );
  });
}
